<!--
  A countdown that a screen reader can survive.

  The number changes every second, and a live region that announces every
  second is unusable, so the ticking text sits outside the accessibility tree
  entirely (`aria-hidden`). The one thing worth announcing is that the wait is
  over, so a separate, silent-until-then `aria-live="polite"` sibling changes
  exactly once, at zero. The initial wait is spoken up front as plain
  visually-hidden text, read once like any other content on the page.
-->
<script setup lang="ts">
import { onUnmounted, ref } from 'vue'

const props = defineProps<{
  seconds: number
}>()

const emit = defineEmits<{
  (e: 'elapsed'): void
}>()

const remaining = ref(props.seconds)
const done = ref(false)

const timer = window.setInterval(() => {
  remaining.value -= 1
  if (remaining.value <= 0) {
    remaining.value = 0
    done.value = true
    window.clearInterval(timer)
    emit('elapsed')
  }
}, 1000)

onUnmounted(() => window.clearInterval(timer))
</script>

<template>
  <p class="text-sm text-ink-muted">
    <span class="sr-only">You can try again in {{ props.seconds }} seconds.</span>
    <span
      v-if="!done"
      aria-hidden="true"
    >Try again in {{ remaining }}s</span>
    <span aria-live="polite">{{ done ? 'You can try again now.' : '' }}</span>
  </p>
</template>
